/** Defines the Zod schema and Firestore data structure for task validation and storage. */
// @ts-check
import { z } from 'zod';
import { format } from 'date-fns';
import { createLoggedStrippedSchema } from '@/features/tasks/utils/data/zodUtils';
import {
  MAX_TASK_TITLE_LENGTH,
  DEFAULT_TASK_DURATION_MINS,
} from '@/config/constants';

/**
 * True for Firestore FieldValue sentinels (serverTimestamp(), arrayUnion(), ...):
 * opaque write-only markers, resolved server-side, never a Timestamp/Date when read back.
 * Duck-typed via `isEqual` (part of the SDK's FieldValue base class) instead of
 * `instanceof FieldValue`, since importing the class ties this schema to the real SDK
 * and breaks under the test mock, which stubs `FieldValue` as a plain object.
 */
const isFieldValueSentinel = (val) =>
  typeof val === 'object' &&
  val !== null &&
  typeof val.isEqual === 'function' &&
  typeof val.toDate !== 'function';

/**
 * Zod schema for createdAt/updatedAt. Accepts:
 * - a resolved Firestore Timestamp/Date (reading a document back)
 * - the serverTimestamp() FieldValue sentinel (writing a new document, unresolved until the server commits it)
 */
const firestoreReadTimestampSchema = z.preprocess(
  (val) => {
    if (val === null || val === undefined) return null;
    if (isFieldValueSentinel(val)) return val;
    if (typeof val === 'object' && typeof val.toDate === 'function')
      return val.toDate();
    if (typeof val === 'object' && 'seconds' in val && 'nanoseconds' in val)
      return new Date(val.seconds * 1000);
    return val;
  },
  z
    .union([z.date(), z.custom(isFieldValueSentinel)])
    .nullable()
    .optional(),
);

const baseTaskShape = {
  userId: z.string(),
  title: z
    .string()
    .trim()
    .min(1, 'Title cannot be empty.')
    .max(
      MAX_TASK_TITLE_LENGTH,
      `Title is too long (max ${MAX_TASK_TITLE_LENGTH} characters).`,
    ),
  description: z.preprocess((val) => {
    if (typeof val === 'string') {
      const trimmed = val.trim();
      return trimmed === '' ? null : trimmed;
    }
    return val;
  }, z.string().nullable().optional()),
  completed: z.boolean(),
  duration: z.number().int().nonnegative(),
  estimatedDuration: z.number().int().nonnegative().optional(),
  measuredDuration: z.number().int().nonnegative().optional(),

  rescheduleCount: z.number().int().nonnegative().default(0),

  /** True until the user sets duration via inbox triage or an explicit duration edit. */
  requireTimeReview: z.boolean().optional(),

  person: z.string().nullable(),
  status: z.enum(['Active', 'Backlog']),
  archived: z.boolean().default(false),

  contexts: z.array(z.string()).optional(),
  projects: z.array(z.string()).optional(),

  recurrence: z
    .object({
      interval: z.number().int().positive(),
      frequency: z.enum(['days', 'weeks', 'months', 'years']),
    })
    .nullable()
    .optional(),

  parentId: z
    .string()
    .nullable()
    .optional()
    .refine((val) => val !== '', {
      message: 'Parent ID cannot be an empty string. Use null for root tasks.',
    }),
  materializedPath: z
    .string()
    .regex(
      /^[a-zA-Z0-9.]*$/,
      'Materialized path can only contain alphanumeric characters and dots.',
    ),
  orderKey: z
    .string()
    .regex(
      /^[a-zA-Z0-9]*$/,
      'Order key can only contain alphanumeric characters.',
    ),

  /**
   * Preprocesses the date input before validating it as a YYYY-MM-DD string.
   *
   * Accepted input formats:
   * - Falsy values (`null`, `""`, `undefined`) -> converted to `null`.
   * - Firestore Timestamp object -> converted to a local 'YYYY-MM-DD' string.
   * - JavaScript `Date` object -> converted to a local 'YYYY-MM-DD' string.
   *
   * @warning Timezone trap! `format()` and `Date` methods use the LOCAL timezone of the user's environment.
   * Can cause 1-day off errors for users in different timezones (e.g., 23:59 UTC might become the next day in UTC+2).
   */
  date: z.preprocess(
    (val) => {
      if (!val || val === '') return null;
      if (
        typeof val === 'object' &&
        val !== null &&
        typeof val.toDate === 'function'
      ) {
        return format(val.toDate(), 'yyyy-MM-dd');
      }
      if (val instanceof Date) {
        return format(val, 'yyyy-MM-dd');
      }
      return val;
    },
    z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
      .nullable()
      .optional(),
  ),
  time: z.preprocess(
    (val) => (val === '' ? null : val),
    z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'Time must be in HH:mm format')
      .nullable()
      .optional(),
  ),

  createdAt: firestoreReadTimestampSchema,
  updatedAt: firestoreReadTimestampSchema,
  completedAt: firestoreReadTimestampSchema,
};

const baseTaskSchema = z.object(baseTaskShape);
/** @public */
export const taskSchema = baseTaskSchema;

// Schema for CREATING a new task.
// It adds default values. Timestamps are handled by the service layer.
const newTaskShapeWithDefaults = {
  ...baseTaskShape,
  completed: z.boolean().default(false),
  duration: z.number().int().positive().default(DEFAULT_TASK_DURATION_MINS),
  person: z.string().nullable().default(null),
  status: z.enum(['Active', 'Backlog']).default('Active'),
  rescheduleCount: z.number().int().nonnegative().default(0),
  archived: z.boolean().default(false),
  requireTimeReview: z.boolean().default(true),
};
export const newTaskSchema = createLoggedStrippedSchema(
  z.object(newTaskShapeWithDefaults),
);

// Schema for UPDATING a task.
// It makes all fields optional.
// The `updatedAt` field is not included here because it's a server-side operation
// added by the mutation hooks, not part of the client-side input.
export const updateTaskSchema = createLoggedStrippedSchema(
  baseTaskSchema.partial().extend({
    id: z.string().optional(),
    updatedAt: z.any().optional(),
  }),
);

/** @typedef {z.infer<typeof taskSchema>} Task */
/** @typedef {z.infer<typeof newTaskSchema>} NewTask */
/** @typedef {z.infer<typeof updateTaskSchema>} UpdateTask */
